/**
 * solubility-table.js — the solubility chart, derived from rules.
 *
 * WHY IT IS COMPUTED AND NOT TYPED
 * --------------------------------
 * The chart is 20 cations × 13 anions = 260 cells. Typed by hand, a wrong cell
 * looks exactly like a right one — and a solubility chart is used precisely
 * when you do not already know the answer, so the reader cannot catch it. So
 * the general rules are stated once, in code, and every deviation is listed
 * explicitly with the compound it belongs to. What you can then review is a
 * page of chemistry instead of a wall of letters.
 *
 * Verdicts follow the Russian school convention, which is also the clearest:
 *   'Р' soluble · 'М' slightly soluble · 'Н' insoluble
 *   '—' does not exist in solution / hydrolyses completely
 *   '?' no reliable data (kept honest rather than guessed)
 *
 * `tests/solubility-table.test.mjs` checks the OUTPUT against textbook values that
 * were not used to build it — AgCl, BaSO₄, CaSO₄, PbI₂, Ca(OH)₂ and so on.
 */
(function (root, factory) {
    var S = factory();
    if (typeof window !== 'undefined') window.SolubilityTable = S;
    if (typeof module !== 'undefined' && module.exports) module.exports = S;
}(this, function () {
    'use strict';

    var CATIONS = [
        { id: 'H', formula: 'H⁺', charge: 1 },
        { id: 'Li', formula: 'Li⁺', charge: 1, alkali: true },
        { id: 'Na', formula: 'Na⁺', charge: 1, alkali: true },
        { id: 'K', formula: 'K⁺', charge: 1, alkali: true },
        { id: 'NH4', formula: 'NH₄⁺', charge: 1, ammonium: true, poly: true },
        { id: 'Mg', formula: 'Mg²⁺', charge: 2, earth: true },
        { id: 'Ca', formula: 'Ca²⁺', charge: 2, earth: true },
        { id: 'Sr', formula: 'Sr²⁺', charge: 2, earth: true },
        { id: 'Ba', formula: 'Ba²⁺', charge: 2, earth: true },
        { id: 'Al', formula: 'Al³⁺', charge: 3, hydrolysing: true },
        { id: 'Cr3', formula: 'Cr³⁺', charge: 3, hydrolysing: true },
        { id: 'Mn', formula: 'Mn²⁺', charge: 2 },
        { id: 'Fe2', formula: 'Fe²⁺', charge: 2 },
        { id: 'Fe3', formula: 'Fe³⁺', charge: 3, hydrolysing: true },
        { id: 'Co', formula: 'Co²⁺', charge: 2 },
        { id: 'Ni', formula: 'Ni²⁺', charge: 2 },
        { id: 'Cu', formula: 'Cu²⁺', charge: 2 },
        { id: 'Zn', formula: 'Zn²⁺', charge: 2 },
        { id: 'Ag', formula: 'Ag⁺', charge: 1 },
        { id: 'Pb', formula: 'Pb²⁺', charge: 2 }
    ];

    // `poly` marks a polyatomic ion: it needs brackets when it is multiplied,
    // which is the difference between Ca(OH)₂ and the nonsense "CaOH₂".
    var ANIONS = [
        { id: 'OH', formula: 'OH⁻', charge: -1, poly: true },
        { id: 'NO3', formula: 'NO₃⁻', charge: -1, poly: true },
        { id: 'F', formula: 'F⁻', charge: -1 },
        { id: 'Cl', formula: 'Cl⁻', charge: -1 },
        { id: 'Br', formula: 'Br⁻', charge: -1 },
        { id: 'I', formula: 'I⁻', charge: -1 },
        { id: 'S', formula: 'S²⁻', charge: -2 },
        { id: 'SO3', formula: 'SO₃²⁻', charge: -2, poly: true },
        { id: 'SO4', formula: 'SO₄²⁻', charge: -2, poly: true },
        { id: 'CO3', formula: 'CO₃²⁻', charge: -2, poly: true },
        { id: 'SiO3', formula: 'SiO₃²⁻', charge: -2, poly: true },
        { id: 'PO4', formula: 'PO₄³⁻', charge: -3, poly: true },
        { id: 'CH3COO', formula: 'CH₃COO⁻', charge: -1, poly: true }
    ];

    /**
     * Deviations from the general rules, each one a compound a chemist would
     * name out loud. This list IS the chart's content; the rules below only
     * fill in the unsurprising majority.
     */
    var EXCEPTIONS = {
        // Halides: soluble except the classic silver and lead precipitates.
        'Ag|Cl': 'Н', 'Ag|Br': 'Н', 'Ag|I': 'Н', 'Ag|F': 'Р',
        'Pb|Cl': 'М', 'Pb|Br': 'М', 'Pb|I': 'Н', 'Pb|F': 'Н',
        // Fluorides break the halide rule for the alkaline earths.
        'Mg|F': 'Н', 'Ca|F': 'Н', 'Sr|F': 'Н', 'Ba|F': 'М', 'Li|F': 'М',
        // Sulfates: the barium test, and its neighbours.
        'Ba|SO4': 'Н', 'Sr|SO4': 'Н', 'Pb|SO4': 'Н', 'Ca|SO4': 'М', 'Ag|SO4': 'М',
        // Hydroxides: alkalis dissolve, the alkaline earths grade from М to Р.
        'Ca|OH': 'М', 'Sr|OH': 'Р', 'Ba|OH': 'Р', 'Mg|OH': 'Н',
        'Ag|OH': '—', 'NH4|OH': 'Р',
        // Sulfides of the alkaline earths, and the ones that hydrolyse away.
        'Mg|S': 'Р', 'Ca|S': 'М', 'Sr|S': 'Р', 'Ba|S': 'Р',
        'Al|S': '—', 'Cr3|S': '—', 'Fe3|S': '—',
        // Carbonates and silicates of the trivalent cations do not survive water.
        'Al|CO3': '—', 'Cr3|CO3': '—', 'Fe3|CO3': '—',
        'Al|SiO3': 'Н', 'Fe3|SiO3': 'Н',
        // Silver acetate is the odd acetate.
        'Ag|CH3COO': 'М',
        // Silicic acid itself is insoluble.
        'H|SiO3': 'Н',
        // The soluble sulfites among the alkaline earths.
        'Ca|SO3': 'М', 'Mg|SO3': 'М',
        // Ammonium carbonate and sulfide are soluble like all its salts.
        'NH4|CO3': 'Р', 'NH4|S': 'Р',
        // Lithium is the alkali metal that breaks the "all alkali salts
        // dissolve" rule — its small ion binds the lattice too tightly.
        // LiF 0.13 g/100 mL, Li₂CO₃ 1.3 g/100 mL, Li₃PO₄ practically insoluble.
        'Li|CO3': 'М', 'Li|PO4': 'Н',
        // Acids themselves: H₂S and H₂SO₃ exist as solutions, H₂CO₃ marginally.
        'H|S': 'Р', 'H|SO3': 'Р', 'H|CO3': 'Р', 'H|OH': 'Р'
    };

    /** The general rules, applied where no exception is listed. */
    function ruleFor(cat, an) {
        // Everything of the alkali metals and ammonium dissolves.
        if (cat.alkali || cat.ammonium) return 'Р';
        // Nitrates and acetates: no exceptions worth teaching.
        if (an.id === 'NO3' || an.id === 'CH3COO') return 'Р';
        // Acids: strong ones are fully dissolved species.
        if (cat.id === 'H') return 'Р';
        // Halides dissolve.
        if (an.id === 'Cl' || an.id === 'Br' || an.id === 'I' || an.id === 'F') return 'Р';
        // Sulfates dissolve.
        if (an.id === 'SO4') return 'Р';
        // Carbonates, silicates, phosphates, sulfites and sulfides do not.
        if (an.id === 'CO3' || an.id === 'SiO3' || an.id === 'PO4' || an.id === 'SO3' || an.id === 'S') return 'Н';
        // Hydroxides do not, unless listed above.
        if (an.id === 'OH') return 'Н';
        return '?';
    }

    function verdict(cationId, anionId) {
        var cat = CATIONS.find(function (c) { return c.id === cationId; });
        var an = ANIONS.find(function (a) { return a.id === anionId; });
        if (!cat || !an) return null;
        var key = cationId + '|' + anionId;
        return Object.prototype.hasOwnProperty.call(EXCEPTIONS, key) ? EXCEPTIONS[key] : ruleFor(cat, an);
    }

    /** Full matrix: rows of cations, columns of anions. */
    function matrix() {
        return CATIONS.map(function (c) {
            return {
                cation: c,
                cells: ANIONS.map(function (a) {
                    return { anion: a, value: verdict(c.id, a.id), formula: formula(c, a) };
                })
            };
        });
    }

    /** Neutral formula of the salt, with the subscripts a textbook would use. */
    var SUB = { 1: '', 2: '₂', 3: '₃', 4: '₄', 5: '₅', 6: '₆' };
    function formula(cat, an) {
        var a = Math.abs(an.charge), b = cat.charge;
        var g = gcd(a, b);
        var nCat = a / g, nAn = b / g;
        var catPart = bare(cat.id);
        var anPart = bareAnion(an.id);
        // Brackets belong to polyatomic ions, on BOTH sides: guessing from the
        // string length gave "CaOH₂" (OH is two characters) and "NH₄₂S".
        var cs = (nCat > 1) ? (cat.poly ? '(' + catPart + ')' + SUB[nCat] : catPart + SUB[nCat]) : catPart;
        var as = (nAn > 1) ? (an.poly ? '(' + anPart + ')' + SUB[nAn] : anPart + SUB[nAn]) : anPart;
        return cs + as;
    }
    function gcd(a, b) { return b ? gcd(b, a % b) : a; }
    function bare(id) {
        return { NH4: 'NH₄', Fe2: 'Fe', Fe3: 'Fe', Cr3: 'Cr' }[id] || id;
    }
    function bareAnion(id) {
        return { OH: 'OH', NO3: 'NO₃', S: 'S', SO3: 'SO₃', SO4: 'SO₄', CO3: 'CO₃', SiO3: 'SiO₃', PO4: 'PO₄', CH3COO: 'CH₃COO' }[id] || id;
    }

    /**
     * The activity series — the other chart every Russian textbook prints on
     * the same page, because the two are used together: one says whether a
     * product precipitates, the other whether the reaction happens at all.
     */
    var ACTIVITY = [
        'Li', 'K', 'Ba', 'Sr', 'Ca', 'Na', 'Mg', 'Al', 'Mn', 'Zn', 'Cr',
        'Fe', 'Cd', 'Co', 'Ni', 'Sn', 'Pb', 'H', 'Cu', 'Hg', 'Ag', 'Pt', 'Au'
    ];
    /** Does metal `a` displace metal `b` from a solution of its salt? */
    function displaces(a, b) {
        var ia = ACTIVITY.indexOf(a), ib = ACTIVITY.indexOf(b);
        if (ia < 0 || ib < 0) return null;
        return ia < ib;
    }

    return {
        CATIONS: CATIONS, ANIONS: ANIONS, EXCEPTIONS: EXCEPTIONS,
        ACTIVITY: ACTIVITY,
        verdict: verdict, matrix: matrix, formula: formula, displaces: displaces
    };
}));
